import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { AMPLITUDE_API_KEY } from "../../services/CONSTANTS";

const APP_NAME = "IFH";

const sessionReplayTracking = sessionReplayPlugin({
  forceSessionTracking: true,
  sampleRate: 1,
});

export const initAmplitude = () => {
  amplitude.add(sessionReplayTracking);
  amplitude.init(AMPLITUDE_API_KEY, {
    defaultTracking: {
      sessions: true
    },
    autocapture: false
  });
};

// Amplitude Event Name Constants
export const AMPLITUDE_EVENTS = {
  // User scheduled a call by booking a call via calendly
  CALL_SCHEDULED_BY_USER: "call_scheduled_by_user",
  // User scheduled a call independently (not via onboarding flow)
  CALL_SCHEDULED_BY_USER_INDEPENDENTLY: "call_scheduled_by_user_independently",
  // User clicked on schedule call button, was shown the caldendly modal to book a slot, but did not book a slot
  USER_DROPPED_OFF_AFTER_CLICKING_SCHEDULE_CALL: "user_dropped_off_after_clicking_schedule_call",
  // User clicked to schedule a call independently (not via onboarding flow)
  USER_CLICKED_ON_SCHEDULE_CALL_INDEPENDENTLY: "user_clicked_on_schedule_call_independently",
  // User completed step 4 of the onboarding walkthrough
  ONBOARDING_WALKTHROUGH_STEP_1_COMPLETED_INDEPENDENTLY: "onboarding_walkthrough_step_1_completed_independently",
  // User started step 1 of the onboarding walkthrough
  ONBOARDING_WALKTHROUGH_STEP_1_STARTED: "onboarding_walkthrough_step_1_started",
  // User completed step 2 of onboarding independently (not via onboarding flow)
  ONBOARDING_WALKTHROUGH_STEP_2_COMPLETED_INDEPENDENTLY: "onboarding_walkthrough_step_2_completed_independently",
  // User started step 2 of the onboarding walkthrough
  ONBOARDING_WALKTHROUGH_STEP_2_STARTED: "onboarding_walkthrough_step_2_started",
  // User completed step 3 of onboarding independently (not via onboarding flow)
  ONBOARDING_WALKTHROUGH_STEP_3_COMPLETED_INDEPENDENTLY: "onboarding_walkthrough_step_3_completed_independently",
  // User started step 3 of the onboarding walkthrough
  ONBOARDING_WALKTHROUGH_STEP_3_STARTED: "onboarding_walkthrough_step_3_started",
  // User skipped the onboarding walkthrough at any step
  ONBOARDING_WALKTHROUGH_STEP_4_COMPLETED_BY_USER: "onboarding_walkthrough_step_4_completed_by_user",
  // User completed step 1 of onboarding independently (not via onboarding flow)
  ONBOARDING_WALKTHROUGH_SKIPPED_BY_USER: "onboarding_walkthrough_skipped_by_user",
  // User was shown the revenue limit exceeded page after their average revenue crossed $10000
  USER_SHOWN_REVENUE_LIMIT_EXCEEDED_PAGE: "user_shown_revenue_limit_exceeded_page",
  // User clicked AI BFCM Planner on Forecasting (handoff into Sam)
  AI_BFCM_PLANNER_BUTTON_CLICKED: "ai_bfcm_planner_button_clicked",
};

// Dedicated tracking functions for each event
// Check 'AMPLITUDE_EVENTS' Constant for description of each event
export const trackAmplitudeOnboardingWalkthroughSkippedByUserEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_SKIPPED_BY_USER, props);

export const trackAmplitudeUserDroppedOffAfterClickingScheduleCallEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.USER_DROPPED_OFF_AFTER_CLICKING_SCHEDULE_CALL, props);

export const trackAmplitudeCallScheduledByUserIndependentlyEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.CALL_SCHEDULED_BY_USER_INDEPENDENTLY, props);

export const trackAmplitudeCallScheduledByUserEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.CALL_SCHEDULED_BY_USER, props);

export const trackAmplitudeUserClickedOnScheduleCallIndependentlyEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.USER_CLICKED_ON_SCHEDULE_CALL_INDEPENDENTLY, props);

export const trackAmplitudeOnboardingWalkthroughStep4CompletedByUserEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_4_COMPLETED_BY_USER, props);

export const trackAmplitudeUserShownRevenueLimitExceededPageEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.USER_SHOWN_REVENUE_LIMIT_EXCEEDED_PAGE, props);

export const trackAmplitudeOnboardingWalkthroughStep2CompletedIndependentlyEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_2_COMPLETED_INDEPENDENTLY, props);

export const trackAmplitudeOnboardingWalkthroughStep3CompletedIndependentlyEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_3_COMPLETED_INDEPENDENTLY, props);

export const trackAmplitudeOnboardingWalkthroughStep1StartedEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_1_STARTED, props);

export const trackAmplitudeOnboardingWalkthroughStep3StartedEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_3_STARTED, props);

export const trackAmplitudeOnboardingWalkthroughStep2StartedEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_2_STARTED, props);

export const trackAmplitudeOnboardingWalkthroughStep1CompletedIndependentlyEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.ONBOARDING_WALKTHROUGH_STEP_1_COMPLETED_INDEPENDENTLY, props);

export const trackAmplitudeAiBfcmPlannerButtonClickedEvent = (props = {}) =>
  trackAmplitudeEvent(AMPLITUDE_EVENTS.AI_BFCM_PLANNER_BUTTON_CLICKED, props);

export const trackAmplitudeEvent = (eventName, eventProperties = {}) => {
  // Always add app_name to eventProperties 
  const propsWithAppName = {
    ...eventProperties,
    app_name: APP_NAME,
  };
  amplitude.track(eventName, propsWithAppName);
};

export const identifyUserForAmplitude = (userId, userProps = {}) => {
  // amplitude.setUserId(userId);

  const identify = new amplitude.Identify();

  // Always set app_name user property
  identify.set('app_name', APP_NAME);

  // Set other user properties
  for (const [key, value] of Object.entries(userProps)) {
    identify.set(key, value);
  }

   amplitude.identify(identify);
};